import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Settings, MapPin } from "lucide-react";
import { isNativeApp } from "@/lib/platform";

interface LocationPermissionDeniedModalProps {
  open: boolean;
  onClose: () => void;
  /**
   * Called when the user taps "Search by city instead". The modal closes
   * automatically before this fires; consumers should focus their own
   * city/zip search input here.
   */
  onSearchInstead?: () => void;
}

export function LocationPermissionDeniedModal({
  open,
  onClose,
  onSearchInstead,
}: LocationPermissionDeniedModalProps) {
  const handleOpenSettings = () => {
    // Attempts native iOS app-settings deep link. Fails silently on
    // unsupported platforms — the modal stays open so instructions remain visible.
    try {
      window.open("app-settings:", "_system");
    } catch {
      // ignore
    }
  };

  const handleSearchInstead = () => {
    onClose();
    // Defer so the dialog unmounts before we move focus to the underlying input.
    setTimeout(() => {
      onSearchInstead?.();
    }, 50);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex justify-center mb-2">
            <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
              <MapPin className="h-6 w-6 text-primary" />
            </div>
          </div>
          <DialogTitle className="text-center">Location access is off</DialogTitle>
          <DialogDescription className="text-center">
            Grounded needs your location to find nearby cafés.
          </DialogDescription>
        </DialogHeader>

        {isNativeApp() ? (
          <ol className="space-y-2 text-sm text-foreground/90 bg-muted/40 rounded-lg p-4 list-decimal list-inside">
            <li>Open your iPhone <span className="font-medium">Settings</span></li>
            <li>Scroll down and tap <span className="font-medium">Grounded</span></li>
            <li>Tap <span className="font-medium">Location</span></li>
            <li>Select <span className="font-medium">While Using the App</span></li>
          </ol>
        ) : (
          <ol className="space-y-2 text-sm text-foreground/90 bg-muted/40 rounded-lg p-4 list-decimal list-inside">
            <li>Click the location or padlock icon in your browser's <span className="font-medium">address bar</span></li>
            <li>Set <span className="font-medium">Location</span> to <span className="font-medium">"Allow"</span></li>
            <li>Tap <span className="font-medium">"Use my location"</span> again</li>
          </ol>
        )}

        <div className="space-y-2 pt-1">
          {isNativeApp() && (
            <Button onClick={handleOpenSettings} className="w-full">
              <Settings className="h-4 w-4 mr-2" />
              Open Settings
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSearchInstead}
            className="w-full text-muted-foreground hover:text-foreground"
          >
            Search by city instead
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
